import { useState } from 'react';
import { useDispatch } from 'react-redux';
import browserHistory from '~/browserHistory';
import { removeJobFromScheduleList } from '~/actions';
import { useCancelJobMutation } from '~/domain/Jobs';
import { fireScheduleListInviteClicked } from '~/posthogs/events';
import { useCurrentShiftCardMenu } from '~/components/shared/useCurrentShiftCardMenu';
import { getProfessionalName } from '~/utils/professional';
import { SHIFT_CARD_STATUS } from '~/types/ShiftCard';
import { mapScheduleJobToShiftCard } from './mapScheduleJobToShiftCard';

// Open shifts invite via the envelope; confirm-candidates invites from the ⋯ menu
// (GOTU-4588). Both are only meaningful before a professional is booked.
const ENVELOPE_INVITE_STATES = new Set([SHIFT_CARD_STATUS.OPEN]);
const MENU_INVITE_STATES = new Set([SHIFT_CARD_STATUS.CONFIRM_CANDIDATES]);
const EDIT_STATES = new Set([SHIFT_CARD_STATUS.OPEN]);
// A booked shift can message the assigned professional instead of editing.
const CHAT_STATES = new Set([SHIFT_CARD_STATUS.SCHEDULED]);
const CANCEL_STATES = new Set([
  SHIFT_CARD_STATUS.OPEN,
  SHIFT_CARD_STATUS.SCHEDULED,
  SHIFT_CARD_STATUS.CONFIRM_CANDIDATES,
]);

/**
 * Drives a schedule-list `JobCard`: maps the raw job to the block props, assembles the
 * per-status overflow menu (invite / edit / chat / cancel), and owns the invite + cancel
 * popup state and the cancel mutation. Keeps the component presentational.
 */
export const useScheduleJobCard = (job) => {
  const dispatch = useDispatch();
  const [isInvitePopupOpen, setInvitePopupOpen] = useState(false);
  const [isCancelPopupOpen, setCancelPopupOpen] = useState(false);
  const { mutate: cancelJob, isPending: isCancelPending } = useCancelJobMutation();

  const cardData = mapScheduleJobToShiftCard(job);
  const { status } = cardData;
  const isInterview = Boolean(job?.isOnsiteInterview);
  const invitedCount = job?.favoriteInvites?.length ?? 0;
  const isCurrent = status === SHIFT_CARD_STATUS.CURRENT;

  // "Current shift" actions (Chat / Rebook / Add to Fav / Report no-show/walk-out) live in
  // a shared hook so the dashboard "Today's Professionals" card (GOTU-5195) can reuse them.
  const receiver = job?.offer_final_reciever;
  const professionalName = getProfessionalName(receiver);
  const profession = job?.profession ?? job?.professionName;
  const currentShift = useCurrentShiftCardMenu({
    jobId: job?.id ? String(job.id) : '',
    shiftStart: job?.job_datetime,
    professionalId: receiver?.id,
    professionalName,
    alreadyReported: Boolean(job?.incident),
    rebook: receiver?.id
      ? {
          user: {
            id: receiver.id,
            name: professionalName ?? '',
            image: receiver.profile_image,
            profession,
            professions: profession ? [profession] : [],
            officeId: job?.offer_owner?.id,
          },
          shiftId: job?.id ? String(job.id) : undefined,
          source: 'schedule_list',
        }
      : undefined,
  });

  const openInvite = () => {
    fireScheduleListInviteClicked({ shiftId: job?.id ? String(job.id) : '' });
    setInvitePopupOpen(true);
  };

  const menuItems = [];
  if (MENU_INVITE_STATES.has(status) && !isInterview) {
    menuItems.push({
      label:
        invitedCount > 0 ? `Invite Professionals (${invitedCount} invited)` : 'Invite Professionals',
      onSelect: openInvite,
    });
  }
  if (EDIT_STATES.has(status)) {
    menuItems.push({
      label: isInterview ? 'Edit Interview' : 'Edit Shift',
      onSelect: () => browserHistory.push(`/dashboard/job/${job?.id}/edit`),
    });
  }
  if (CHAT_STATES.has(status)) {
    menuItems.push({
      // "Chat" to match the current-shift / today's-professionals menu wording.
      label: 'Chat',
      onSelect: () => browserHistory.push(`/dashboard/chat/${job?.id}`),
    });
  }
  if (CANCEL_STATES.has(status)) {
    menuItems.push({
      label: isInterview ? 'Cancel Interview' : 'Cancel Shift',
      onSelect: () => setCancelPopupOpen(true),
    });
  }

  return {
    cardData,
    isInterview,
    // Current shift: Chat / Add-to-Fav menu + the warning-icon report flow (no envelope).
    // Other statuses: the invite/edit/chat/cancel menu built above.
    handleInvite:
      !isCurrent && ENVELOPE_INVITE_STATES.has(status) && !isInterview ? openInvite : undefined,
    menuItems: isCurrent ? currentShift.menuItems : menuItems.length > 0 ? menuItems : undefined,
    onReportIncident: isCurrent ? currentShift.onReportIncident : undefined,
    incidentReported: isCurrent ? currentShift.incidentReported : undefined,
    canReportIncident: isCurrent ? currentShift.canReportIncident : undefined,
    rebookPopup: isCurrent ? currentShift.rebookPopup : null,
    isInvitePopupOpen,
    closeInvitePopup: () => setInvitePopupOpen(false),
    isCancelPopupOpen,
    closeCancelPopup: () => setCancelPopupOpen(false),
    confirmCancel: () => {
      setCancelPopupOpen(false);
      // The cancel mutation only invalidates React Query; the schedule list is Redux-backed,
      // so drop the cancelled shift from the list once the cancel succeeds.
      cancelJob(
        { jobId: job?.id },
        { onSuccess: () => dispatch(removeJobFromScheduleList(job?.id)) },
      );
    },
    isCancelPending,
  };
};
